import type { TrackPoint, KartState, ItemBoxState } from '@shared/types';
import type { InitializedTrack } from '@shared/track';

// ============================================================
// Minimap -- 2D canvas overlay showing track, karts, and items
// ============================================================

const CANVAS_SIZE = 200;
const PADDING = 16;
const DRAW_SIZE = CANVAS_SIZE - PADDING * 2;

export class Minimap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Track data (set once per race)
  private trackPath: TrackPoint[] = [];
  private itemPositions: Array<{ x: number; z: number }> = [];

  // Coordinate transform: world -> minimap
  private offsetX = 0;
  private offsetZ = 0;
  private scale = 1;

  constructor() {
    this.canvas = document.getElementById('minimap-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;

    // Support high-DPI displays
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = CANVAS_SIZE * dpr;
    this.canvas.height = CANVAS_SIZE * dpr;
    this.ctx.scale(dpr, dpr);
  }

  // ────────────────────────────────────────────────────────────
  // Set track data (call once when a race starts)
  // ────────────────────────────────────────────────────────────

  setTrack(track: InitializedTrack): void {
    this.trackPath = track.path;
    this.itemPositions = track.itemPositions;

    // Compute bounding box of track path
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;

    for (const p of this.trackPath) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.z < minZ) minZ = p.z;
      if (p.z > maxZ) maxZ = p.z;
    }

    const rangeX = maxX - minX || 1;
    const rangeZ = maxZ - minZ || 1;

    // Uniform scale so the track is not stretched
    this.scale = DRAW_SIZE / Math.max(rangeX, rangeZ);
    this.offsetX = minX + rangeX / 2;
    this.offsetZ = minZ + rangeZ / 2;
  }

  // ────────────────────────────────────────────────────────────
  // Transform world coordinates to minimap-local coordinates
  // (before canvas rotation -- centered at origin)
  // ────────────────────────────────────────────────────────────

  private toLocal(wx: number, wz: number): { lx: number; lz: number } {
    return {
      lx: (wx - this.offsetX) * this.scale,
      lz: (wz - this.offsetZ) * this.scale,
    };
  }

  // ────────────────────────────────────────────────────────────
  // Draw one frame (called from the game loop)
  // ────────────────────────────────────────────────────────────

  update(
    localKart: KartState | null,
    allKarts: KartState[],
    items: ItemBoxState[]
  ): void {
    const ctx = this.ctx;
    const half = CANVAS_SIZE / 2;

    // Clear
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Draw background
    ctx.fillStyle = 'rgba(10, 14, 26, 0.85)';
    ctx.beginPath();
    ctx.roundRect(0, 0, CANVAS_SIZE, CANVAS_SIZE, 12);
    ctx.fill();

    // Save state before rotation
    ctx.save();

    // Translate to center, then rotate so the player faces up
    ctx.translate(half, half);
    if (localKart) {
      // yaw = 0 means pointing +X in the game world.
      // We want "facing up" on the minimap (negative Y in canvas coords).
      // Rotate the map so that the player's forward direction points up.
      ctx.rotate(-localKart.yaw + Math.PI / 2);
    }

    // If we have a local kart, also shift so the player is centered
    if (localKart) {
      const lk = this.toLocal(localKart.x, localKart.z);
      ctx.translate(-lk.lx, -lk.lz);
    }

    // ── Track outline ──
    if (this.trackPath.length > 1) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();

      // Draw every Nth point to keep the path smooth but light on draw calls
      const step = Math.max(1, Math.floor(this.trackPath.length / 300));
      const first = this.toLocal(this.trackPath[0].x, this.trackPath[0].z);
      ctx.moveTo(first.lx, first.lz);

      for (let i = step; i < this.trackPath.length; i += step) {
        const p = this.toLocal(this.trackPath[i].x, this.trackPath[i].z);
        ctx.lineTo(p.lx, p.lz);
      }

      ctx.closePath();
      ctx.stroke();
    }

    // ── Item boxes (active ones as small yellow dots) ──
    for (const item of items) {
      if (!item.active) continue;
      const p = this.toLocal(item.x, item.z);
      ctx.fillStyle = 'rgba(255, 221, 68, 0.7)';
      ctx.beginPath();
      ctx.arc(p.lx, p.lz, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Kart dots (other karts first, then player on top) ──
    for (const kart of allKarts) {
      if (kart.id === localKart?.id) continue;

      const p = this.toLocal(kart.x, kart.z);
      const color = '#' + kart.color.toString(16).padStart(6, '0');

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(p.lx, p.lz, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Player dot (larger, with direction indicator) ──
    if (localKart) {
      const p = this.toLocal(localKart.x, localKart.z);
      const color = '#' + localKart.color.toString(16).padStart(6, '0');

      // Glow
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;

      // Dot
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(p.lx, p.lz, 5, 0, Math.PI * 2);
      ctx.fill();

      // Inner colored dot
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(p.lx, p.lz, 3.5, 0, Math.PI * 2);
      ctx.fill();

      // Direction triangle
      ctx.shadowBlur = 0;
      const yaw = localKart.yaw;
      const triSize = 7;
      const tipX = p.lx + Math.cos(yaw) * triSize;
      const tipZ = p.lz + Math.sin(yaw) * triSize;
      const backL_X = p.lx + Math.cos(yaw + 2.5) * 4;
      const backL_Z = p.lz + Math.sin(yaw + 2.5) * 4;
      const backR_X = p.lx + Math.cos(yaw - 2.5) * 4;
      const backR_Z = p.lz + Math.sin(yaw - 2.5) * 4;

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(tipX, tipZ);
      ctx.lineTo(backL_X, backL_Z);
      ctx.lineTo(backR_X, backR_Z);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  // ────────────────────────────────────────────────────────────
  // Clear (call when race ends)
  // ────────────────────────────────────────────────────────────

  clear(): void {
    this.trackPath = [];
    this.itemPositions = [];
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
